/**
 * Single source of truth for UI languages in Promang.
 *
 * - `code`: i18next resource key (what `i18n.changeLanguage` receives).
 * - `apiLocale`: value stored in `users.locale` via the user settings form.
 * - `aliases`: other values that may arrive from the API, settings or deep links.
 * - `dateFnsLocale`: export name in `date-fns/locale` (resolved in `./dateLocales.js`,
 *   kept out of this file so it stays importable without the date-fns ESM bundle).
 * - `addedAt` + `announce`: when a new language ships, set both. Every user who
 *   has not answered yet gets the "Ny funksjon" language prompt once
 *   (see `src/app/featureAnnouncements/registry.js`).
 *
 * Adding a language: add `src/translation/<code>.js`, register it in `lazyLanguages` in `src/i18n.js`,
 * add an entry here with `announce: true`, and add the API locale (`config.i18n.available_locales`).
 */
export const SUPPORTED_LANGUAGES = [
  {
    code: "nb",
    apiLocale: "no",
    aliases: ["nb", "no", "nn"],
    nativeName: "Norsk",
    intl: "nb-NO",
    dateFnsLocale: "nb",
  },
  {
    code: "en",
    apiLocale: "en",
    aliases: ["en"],
    nativeName: "English",
    intl: "en-GB",
    dateFnsLocale: "enGB",
  },
  {
    code: "se",
    apiLocale: "se",
    aliases: ["se", "sv"],
    nativeName: "Svenska",
    intl: "sv-SE",
    dateFnsLocale: "sv",
  },
  {
    code: "pl",
    apiLocale: "pl",
    aliases: ["pl"],
    nativeName: "Polski",
    intl: "pl-PL",
    dateFnsLocale: "pl",
    addedAt: "2026-10-11",
    announce: true,
  },
];

const DEFAULT_LANGUAGE = SUPPORTED_LANGUAGES[0];

/** Resolve any stored/API/i18n language value to a registry entry (default: Norsk). */
export function findLanguage(value) {
  const raw = String(value || "").trim().toLowerCase();
  if (!raw) return null;
  const base = raw.split(/[-_]/)[0];
  return (
    SUPPORTED_LANGUAGES.find((lang) => lang.code === raw || lang.aliases.includes(raw)) ||
    SUPPORTED_LANGUAGES.find((lang) => lang.aliases.includes(base)) ||
    null
  );
}

export function resolveLanguage(value) {
  return findLanguage(value) || DEFAULT_LANGUAGE;
}

/** Map a Rails `users.locale` value to the i18next resource code. */
export function i18nCodeFromApiLocale(locale, fallback = "en") {
  return findLanguage(locale)?.code || fallback;
}

/** BCP 47 tag for `Intl` / `toLocaleDateString`. */
export function intlLocaleFor(language) {
  return resolveLanguage(language).intl;
}

/** Languages that should be announced to existing users (newest first). */
export function announcedLanguages() {
  return SUPPORTED_LANGUAGES.filter((lang) => lang.announce && lang.addedAt).sort((a, b) =>
    b.addedAt.localeCompare(a.addedAt),
  );
}
